import type { Node } from '@xyflow/react';
import {
  ActionNodeData,
  EnrollNodeData,
  MappedActionNode,
  MappedEnrollNode,
  MappedWaitNode,
  MappedWorkflowNode,
  MapperRegistryType,
  WaitNodeData,
  WorkflowNode,
} from './map-canvas-content.types';
import { createCanvasMapperRegistry } from './map-canvas-content.registries';
import { NodeType } from '../hooks/use-sidebar-control/use-sidebar-control.types';
import {
  AndBlockValues,
  EntityType,
} from '../workflow-sidebar/edit-node-form/nodes/enroll-node-editor/enroll-node-editor.types';

const CANVAS_MAPPER_REGISTRY: MapperRegistryType = createCanvasMapperRegistry({
  mapEnrollNode: (node) => ({
    id: node.id,
    ...mapEnrollNode(node),
  }),
  mapWaitNode: (node) => ({
    id: node.id,
    ...mapWaitNode(node),
  }),
  mapActionNode: (node) => ({
    id: node.id,
    ...mapActionNode(node),
  }),
});

export const mapCanvasContent = ({ nodes }: { nodes: Node[] }): MappedWorkflowNode[] => {
  return nodes.filter(isMappableNode).map((node) => {
    const mapper = CANVAS_MAPPER_REGISTRY[node.type] as (n: typeof node) => MappedWorkflowNode;
    return mapper(node);
  });
};

function mapEnrollNode(node: Node<EnrollNodeData>): Omit<MappedEnrollNode, 'id'> {
  const rules = {
    OR: node.data.orBlocks.map((or) => ({
      AND: or.andBlocks.filter(hasEntityType).map((and) => {
        if (and.entityType === 'branchOfficeIds') {
          const mapped = {
            key: 'branchOfficeIds' as const,
            value: and.entityId.map((id) => id.value),
            condition: 'equal' as const,
          };
          return mapped;
        }
        const mapped = {
          key: 'jobTypeIds' as const,
          value: and.entityId.map((id) => id.value),
          condition: 'equal' as const,
        };
        return mapped;
      }),
    })),
  };

  return { type: NodeType.Enroll, rules };
}

function mapWaitNode(node: Node<WaitNodeData>): Omit<MappedWaitNode, 'id'> {
  return {
    type: NodeType.Wait,
    rules: {
      key: 'percentage',
      condition: 'greaterThan',
      value: Number(node.data.percentage),
    },
  };
}

function mapActionNode(_node: Node<ActionNodeData>): Omit<MappedActionNode, 'id'> {
  return {
    type: NodeType.Action,
    rules: {},
  };
}

const isMappableNode = (node: Node): node is WorkflowNode => {
  return !!node.type && node.type in CANVAS_MAPPER_REGISTRY;
};

const hasEntityType = (and: AndBlockValues): and is AndBlockValues & { entityType: EntityType } => {
  return Boolean(and.entityType);
};
